
import React, { useMemo } from 'react';
import { useRememberedState } from '../../hooks/useRemembered';
import { UserProfile, ReportMetadata, AlertConfig } from '../../types';
import { contractedQuantity } from '../../services/nepPosition';
import { istSifaProjekt } from '../../services/projectType';
import { Button } from '../Button';
import { AlertTriangle, Clock, CheckCircle, Settings2, FileWarning, Archive, Filter, User, FileDown, Hash, Receipt, Ban } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useUnbookedSentReports } from '../../hooks/useUnbookedSentReports';
import {
  isValidExemptReason,
  sortUnbookedReports,
  unbookedAlertTitle,
  unbookedDetail,
  unbookedPriority,
} from '../../services/unbookedReportsLogic';
import { getServiceLog } from '../../services/serviceJournal';
import { inspectionQuota, latestInspectionLogDate } from '../../services/inspectionFigures';
import { AdminForeignAccessList } from './AdminForeignAccessList';
import type { ListProject } from '../../services/listProject';

interface Props {
  projects: ListProject[];
  users: UserProfile[];
  currentUser: UserProfile | null;
  onOpenProject: (project: ListProject) => void;
  onOpenAlertSettings: () => void;
  onOpenSupplement: (project: ListProject) => void;
  alertConfig?: AlertConfig;
}

/**
 * Eine Zeile der Meldungsliste. `project` ist der Listeneintrag, kein volles
 * Projekt — er reicht fuer Anzeige und Sprung; der Nachtrag laedt selbst
 * frisch (projectSupplement.ts).
 */
interface Meldung {
  id: string;
  type: string;
  title: string;
  project: ListProject;
  severity: string;
  filterLabel?: string;
  detail?: string;
  reportHistoryId?: string;
}

// DEFINE DEFAULTS GLOBALLY TO ENSURE STABILITY
const DEFAULT_CONFIG: AlertConfig = {
    enableOverrun: true,
    enableQuota: true,
    quotaThreshold: 2,
    enableStartDocs: true,
    enableLatency: true,
    latencyDays: 20
};

export const AdminAlertsView: React.FC<Props> = ({ projects, users, currentUser, onOpenProject, onOpenAlertSettings, onOpenSupplement, alertConfig }) => {

  const [typeFilter, setTypeFilter] = useRememberedState<string>('admin.alerts.type', 'all');

  // ── Versendeter Bericht ohne Leistungsbuchung (MIGRATIONS.md Nr. 45) ──────
  //
  // Warum diese Meldung hier steht: sie ist die einzige der Liste, die Geld
  // kostet, ohne dass jemand etwas tun muesste — und aufgefallen ist sie
  // dreimal erst BEI DER RECHNUNG (26071015, 26041014, 6051). Genau darauf
  // sieht diese Ansicht: sie ist der Sammelblick der Verwaltung vor dem
  // Rechnungslauf und hat als einzige einen PDF-Export. Bis 20.09.2026 stand
  // derselbe Fall auch in der Aufgabenliste des Koordinators; seitdem sind
  // Meldungsliste und Integritaetswaechter die einzigen Anzeigeorte.
  //
  // Die Lage kommt serverseitig aus `v_unbooked_sent_reports` und nicht aus
  // `projects` — sie faellt erst aus dem Abgleich von Berichtsarchiv,
  // Sendeverlauf und Protokoll. Deshalb ein eigener Ladepfad neben dem
  // useMemo unten und keine Erweiterung darin.
  const { rows: unbookedRows, busyId: unbookedBusyId, markExempt } = useUnbookedSentReports();

  /**
   * Ausweg 2: „nicht abrechenbar". Ausweg 1 ist das Nachbuchen im Projekt
   * (Knopf „Projekt öffnen"); die Meldung verschwindet dann von allein, weil
   * die Sicht das Protokoll liest und keinen zweiten Merker fuehrt.
   *
   * Der Grund ist Pflicht — dieselbe Wache steht in der RPC. Eine Ausnahme
   * ohne Begruendung ist im Streitfall wertlos.
   */
  const handleMarkNotBillable = async (reportHistoryId: string, title: string, projectName: string) => {
      const reason = window.prompt(
          `${title}\n\nProjekt: ${projectName}\n\n`
          + 'Dieser Bericht wird ausdrücklich NICHT abgerechnet. Bitte den Grund angeben '
          + '(er wird mit Zeitpunkt und Person im Archiv festgehalten):',
          '',
      );
      if (reason === null) return;
      if (!isValidExemptReason(reason)) {
          alert('Ohne Grund lässt sich die Ausnahme nicht setzen.');
          return;
      }
      try {
          await markExempt(reportHistoryId, reason.trim());
      } catch (e) {
          console.error('billing_exempt konnte nicht gesetzt werden:', e);
          alert('Die Ausnahme konnte nicht gespeichert werden: ' + ((e as any)?.message || 'Unbekannter Fehler'));
      }
  };

  // Nur Faelle zu Projekten, die in dieser Ansicht ueberhaupt vorliegen: die
  // Knoepfe „Projekt öffnen" und „Nachtrag" brauchen das Projektobjekt. Ein
  // Fall ohne Projekt in der Liste waere eine Meldung, die niemand bedienen
  // kann. (Die Sicht selbst trennt bereits nach Zugriff.)
  const unbookedAlerts = useMemo(() => {
      const byId = new Map<string, ListProject>((projects || []).map(p => [p.id, p]));
      return sortUnbookedReports(unbookedRows)
          .filter(r => byId.has(r.project_id))
          .map(r => ({
              id: `unbooked_${r.report_history_id}`,
              type: 'unbooked_report',
              // Der Filter oben gruppiert nach Typ — ohne festen Text stuende
              // dort die Berichtsnummer des ersten Treffers.
              filterLabel: 'Bericht ohne Abrechnung',
              title: unbookedAlertTitle(r),
              detail: unbookedDetail(r),
              project: byId.get(r.project_id),
              reportHistoryId: r.report_history_id,
              severity: unbookedPriority(r) === 'urgent' ? 'high' : 'medium',
          }));
  }, [unbookedRows, projects]);

  const projectAlerts = useMemo(() => {
      const now = new Date();
      now.setHours(0,0,0,0);
      
      const config = { ...DEFAULT_CONFIG, ...alertConfig };

      // Filter visible projects
      let list = projects;
      if (currentUser?.role !== 'super_admin') {
          (currentUser?.company_name || '').trim().toLowerCase();
          list = list.filter(_p => {
               // We assume 'projects' prop is already filtered by AdminView based on company if needed.
               return true; 
          });
      }

      return list.flatMap(p => {
          const alerts: Meldung[] = [];

          const meta = p.data?.metadata as ReportMetadata | undefined;
          
          if (!meta) return [];

          const isReportExempt = meta.globalData?.reportExempt === true;
          // SiFa = unbefristete Betreuung ohne Begehungsturnus und ohne
          // Startdokumente einer Baustelle: Kontingent, Vorankuendigung und
          // Liegezeit sind dort keine Befunde (docs/sifa-betreuung-plan.md).
          const isSifa = istSifaProjekt(meta);

          const services = meta.globalData?.services;
          const pStatus = p.status || meta.globalData?.status;
          
          // ARCHIV: Schlussrechnung gestellt und bezahlt — nichts mehr zu melden.
          if (pStatus === 'finished') return alerts;
          // BEENDET: die Schlussrechnung steht aus. Sonst nichts — vor Ort ist
          // die Arbeit vorbei.
          if (pStatus === 'completed') {
              alerts.push({
                  id: p.id + '_finished',
                  type: 'project_finished',
                  title: 'Projekt beendet - Schlussrechnung stellen',
                  project: p,
                  severity: 'low'
              });
              return alerts;
          }

          if (services && Array.isArray(services)) {
              // 1. Vertragsmenge überschritten (Contract Exceeded)
              if (config.enableOverrun) {
                  const hasOverrun = services.some(s => {
                      const used = Number(s.quantityUsed) || 0;
                      // NEP: beauftragt ist, was gebucht ist — eine
                      // Eventualposition ueberschreitet nie ihren Vordersatz.
                      const contracted = contractedQuantity(s);
                      // Logic: Used must be strictly greater than contracted
                      return used > contracted;
                  });

                  if (hasOverrun) {
                      alerts.push({
                          id: p.id + '_overrun', 
                          type: 'overrun', 
                          title: 'Leistungs-Nachtrag erforderlich', 
                          project: p,
                          severity: 'high'
                      });
                  }
              }
              
              // 2. Restmenge erreicht (Quota Warning)
              if (config.enableQuota && !isReportExempt && !isSifa) {
                  // Keine NEP-Position als Turnusposition: ihr Rest ist kein Kontingent.
                  // Keine Pauschale (Monate), und deren Nachweiszeilen zaehlen als
                  // Begehungen der Turnusposition (inspectionFigures).
                  const quota = inspectionQuota(services, getServiceLog({ metadata: meta }));
                  if (quota) {
                      const { contracted, remaining } = quota;
                      
                      // Only warn if contracted exists (>0) AND remaining is low/zero but not overrun (overrun has its own alert)
                      if (contracted > 0 && remaining >= 0 && remaining <= (config.quotaThreshold || 2)) {
                          alerts.push({
                              id: p.id + '_quota',
                              type: 'quota_warning',
                              title: `Begehungskontingent knapp (Rest: ${remaining})`,
                              project: p,
                              severity: 'medium'
                          });
                      }
                  }
              }

              // 3. Fehlende Dokumente zum Start (Missing Docs)
              if (config.enableStartDocs && !isSifa) {
                  const startDateStr = meta.globalData?.startDate;
                  if (startDateStr) {
                      const startDate = new Date(startDateStr);
                      startDate.setHours(0,0,0,0);
                      
                      // Only check if project start date has passed
                      if (now >= startDate) {
                          const vorank = services.find(s => s.lvPosition === '2.1.10' || s.assignment === 'vorank' || s.title.toLowerCase().includes('vorankündigung'));
                          const unterlage = services.find(s => s.lvPosition === '1.4.10' || s.assignment === 'unterlage' || s.title.toLowerCase().includes('unterlage'));
                          
                          const missingVorank = vorank && ((Number(vorank.quantityUsed) || 0) === 0 && vorank.status !== 'done' && vorank.status !== 'billed');
                          const missingUnterlage = unterlage && ((Number(unterlage.quantityUsed) || 0) === 0 && unterlage.status !== 'done' && unterlage.status !== 'billed');
                          
                          if (missingVorank || missingUnterlage) {
                               const docs = [];
                               if (missingVorank) docs.push("Vorankündigung");
                               if (missingUnterlage) docs.push("Unterlage");
                               
                               alerts.push({
                                  id: p.id + '_docs_missing',
                                  type: 'start_doc_missing',
                                  title: `Fehlende Dokumente (${docs.join(', ')})`,
                                  project: p,
                                  severity: 'high'
                               });
                          }
                      }
                  }
              }
          }
          
          // 4. Inaktivität (Latency)
          if (config.enableLatency && !isReportExempt && !isSifa) {
              // Juengste Begehungszeile im Protokoll (Buchung auf einer
              // Begehungsposition oder Nachweis auf der Pauschale) —
              // inspectionFigures, wortgleich mit dem Zaehler in AdminView.
              const latestManualDate = latestInspectionLogDate(services, getServiceLog({ metadata: meta }));

              const lastReportDateStr = p.last_inspection_date || meta.date;
              let lastDate = lastReportDateStr ? new Date(lastReportDateStr) : null;
              if (lastDate && isNaN(lastDate.getTime())) lastDate = null;
              
              // Use the more recent date between reports and manual entries
              if (latestManualDate && (!lastDate || latestManualDate > lastDate)) {
                  lastDate = latestManualDate;
              }

              if (lastDate) {
                  lastDate.setHours(0,0,0,0);
                  const diffTime = now.getTime() - lastDate.getTime();
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  
                  if (diffDays > (config.latencyDays || 20)) {
                      alerts.push({
                          id: p.id + '_latency',
                          type: 'inspection_latency',
                          title: `Keine Begehung seit ${diffDays} Tagen`,
                          project: p,
                          severity: 'medium'
                      });
                  }
              }
          }
          return alerts;
      });
  }, [projects, currentUser, alertConfig]);

  // Die Berichtsmeldungen ZUERST: sie sind die einzigen, bei denen bereits
  // erbrachte Leistung auf keinem Beleg liegt.
  const globalAlerts = useMemo<Meldung[]>(
      () => [...unbookedAlerts, ...projectAlerts],
      [unbookedAlerts, projectAlerts],
  );

  const filteredAlerts = useMemo(() => {
      if (typeFilter === 'all') return globalAlerts;
      return globalAlerts.filter(alert => alert.type === typeFilter);
  }, [globalAlerts, typeFilter]);

  const alertTypes = useMemo(() => {
      const types = new Map<string, string>();
      globalAlerts.forEach(a => {
          if (!types.has(a.type)) {
              types.set(a.type, a.filterLabel || a.title.split(' (')[0].split(' - ')[0]);
          }
      });
      return Array.from(types.entries());
  }, [globalAlerts]);

  const handlePdfExport = () => {
      const doc = new jsPDF();
      const now = new Date().toLocaleString('de-DE');
      
      doc.setFontSize(18);
      doc.text('Meldungsübersicht', 14, 20);
      doc.setFontSize(10);
      doc.text(`Erstellt am: ${now}`, 14, 28);
      doc.text(`Filter: ${typeFilter === 'all' ? 'Alle Typen' : alertTypes.find(t => t[0] === typeFilter)?.[1] || typeFilter}`, 14, 34);

      const tableData = filteredAlerts.map(alert => {
          const owner = users.find(u => u.id === alert.project.user_id);
          const pNum = alert.project.project_number || alert.project.data?.metadata?.projectNumber || '-';
          return [
              alert.project.name,
              pNum,
              owner ? `${owner.first_name} ${owner.last_name}` : '-',
              alert.title
          ];
      });

      autoTable(doc, {
          startY: 40,
          head: [['Projekt', 'Projekt-Nr.', 'Besitzer', 'Meldung']],
          body: tableData,
          theme: 'striped',
          headStyles: { fillColor: [79, 70, 229] }, // indigo-600
          styles: { fontSize: 9 }
      });

      doc.save(`Meldungen_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  return (
    <div className="space-y-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
            <div className="flex items-center gap-2">
                <div className="bg-red-50 p-1.5 rounded-lg border border-red-100">
                    <AlertTriangle className="w-5 h-5 text-red-600"/>
                </div>
                <h3 className="font-bold text-slate-800">Aktuelle Meldungen ({filteredAlerts.length})</h3>
            </div>
            
            <div className="flex items-center gap-2 w-full md:w-auto">
                <Button 
                    variant="secondary" 
                    onClick={handlePdfExport} 
                    disabled={filteredAlerts.length === 0}
                    className="bg-white border border-slate-300 text-slate-600 hover:text-indigo-600 hover:border-indigo-300" 
                    icon={<FileDown className="w-4 h-4"/>}
                >
                    PDF Export
                </Button>
                <div className="relative flex-1 md:flex-none">
                    <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <select 
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="pl-9 pr-8 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-600 outline-none focus:ring-2 focus:ring-indigo-500 appearance-none cursor-pointer hover:border-slate-300 transition-all w-full"
                    >
                        <option value="all">Alle Typen</option>
                        {alertTypes.map(([type, label]) => (
                            <option key={type} value={type}>{label}</option>
                        ))}
                    </select>
                </div>
                <Button variant="secondary" onClick={onOpenAlertSettings} className="bg-white border border-slate-300 text-slate-600 hover:text-blue-600 hover:border-blue-300" icon={<Settings2 className="w-4 h-4"/>}>Konfiguration</Button>
            </div>
        </div>
        
        {filteredAlerts.map((alert, idx) => {
            const isOverrun = alert.type === 'overrun';
            const isStartMissing = alert.type === 'start_doc_missing';
            const isFinished = alert.type === 'project_finished';
            const isUnbooked = alert.type === 'unbooked_report';
            
            const owner = users.find(u => u.id === alert.project.user_id);
            
            let borderColor = 'border-orange-400';
            let bgColor = 'bg-white';
            let textColor = 'text-orange-600';
            let icon = <Clock className="w-4 h-4"/>;

            if (isOverrun || isStartMissing) {
                borderColor = 'border-red-500';
                bgColor = 'bg-red-50/50';
                textColor = 'text-red-700';
                icon = isOverrun ? <AlertTriangle className="w-4 h-4"/> : <FileWarning className="w-4 h-4"/>;
            } else if (isFinished) {
                borderColor = 'border-purple-400';
                bgColor = 'bg-purple-50';
                textColor = 'text-purple-700';
                icon = <Archive className="w-4 h-4"/>;
            } else if (isUnbooked) {
                // Erbrachte Leistung liegt auf keinem Beleg — dieselbe Stufe
                // wie eine ueberschrittene Vertragsmenge, aus demselben Grund.
                borderColor = 'border-red-500';
                bgColor = 'bg-red-50/50';
                textColor = 'text-red-700';
                icon = <Receipt className="w-4 h-4"/>;
            }

            return (
                <div key={idx} className={`border-l-4 rounded-r-xl p-4 shadow-sm flex justify-between items-center ${borderColor} ${bgColor}`}>
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <div className="flex flex-col">
                                <h4 className="font-bold text-slate-800 leading-tight">{alert.project.name}</h4>
                                <div className="flex items-center gap-1 text-[11px] text-slate-500 font-medium mt-0.5">
                                    <Hash className="w-3 h-3" />
                                    <span>{alert.project.project_number || alert.project.data?.metadata?.projectNumber || 'Keine Nr.'}</span>
                                </div>
                            </div>
                            {owner && (
                                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-100 rounded-full border border-slate-200">
                                    <User className="w-3 h-3 text-slate-500" />
                                    <span className="text-[11px] font-bold text-slate-600">{owner.first_name} {owner.last_name}</span>
                                </div>
                            )}
                        </div>
                        <p className={`text-sm font-medium flex items-center gap-2 ${textColor}`}>
                            {icon} {alert.title}
                        </p>
                        {isOverrun && <p className="text-xs text-red-600 mt-1 ml-6">Die beauftragte Menge wurde überschritten.</p>}
                        {isStartMissing && <p className="text-xs text-red-600 mt-1 ml-6">Projekt ist gestartet, aber Dokumente fehlen.</p>}
                        {isFinished && <p className="text-xs text-purple-600 mt-1 ml-6">Das Projekt ist beendet. Bitte offene Posten prüfen und die Schlussrechnung stellen — nach Zahlungseingang kann ein Administrator archivieren.</p>}
                        {isUnbooked && <p className="text-xs text-red-600 mt-1 ml-6">{alert.detail}</p>}
                        {isUnbooked && <p className="text-[11px] text-slate-500 mt-0.5 ml-6">Der Bericht ist beim Kunden, die Leistung steht auf keinem Beleg. Entweder nachbuchen oder ausdrücklich als nicht abrechenbar kennzeichnen.</p>}
                    </div>
                    <div className="flex gap-2">
                        {isOverrun && (
                            <Button variant="primary" onClick={() => onOpenSupplement(alert.project)} className="text-xs h-8 bg-red-600 hover:bg-red-700 border-transparent shadow-sm">
                                Nachtrag
                            </Button>
                        )}
                        {/* Die zwei Auswege. Einen dritten — wegklicken — gibt es
                            bewusst nicht: genau der hat die Luecke offen gehalten. */}
                        {isUnbooked && (
                            <Button
                                variant="secondary"
                                onClick={() => void handleMarkNotBillable(alert.reportHistoryId, alert.title, alert.project?.name || '')}
                                disabled={unbookedBusyId === alert.reportHistoryId}
                                className="text-xs h-8 border-amber-300 text-amber-700 hover:bg-amber-50"
                                icon={<Ban className="w-3.5 h-3.5"/>}
                            >
                                Nicht abrechenbar
                            </Button>
                        )}
                        <Button variant="secondary" onClick={() => onOpenProject(alert.project)} className="text-xs h-8">
                            Projekt öffnen
                        </Button>
                    </div>
                </div>
            );
        })}
        {filteredAlerts.length === 0 && (<div className="text-center py-12 bg-white rounded-xl border border-dashed border-slate-200"><CheckCircle className="w-12 h-12 text-emerald-200 mx-auto mb-3" /><p className="text-slate-500 font-medium">Keine Warnungen vorhanden.</p><p className="text-xs text-slate-400">Alle Projekte liegen im Zeit- und Kostenplan.</p></div>)}
        {/* Fremdzugriffe mit Begruendung (SQL Nr. 128) — derselbe Sammelblick. */}
        <AdminForeignAccessList projects={projects} onOpenProject={onOpenProject} />
    </div>
  );
};
